/**
 * The long-form page behind the evolution chart's (i).
 *
 * Same shape as the threat page: three sections shortest first, equations in
 * `<details>` rather than in the paragraphs, and the interactive piece last.
 *
 * **The page is the selected branch's**, and here that is not a matter of
 * swapping a few numbers — the branches do not price evolution the same *way*.
 * Live sums a loop whose per-point price is a line times a compounding
 * surcharge; the other two evaluate a power law with no loop at all. So there
 * are two sets of sections, and the branch picks one. What they share is the
 * question each section answers, in the same order, under the same three
 * anchors — `#price`, `#regions`, `#explorer` — so an (i) that points at one of
 * them lands in the right place whichever branch is up.
 *
 * Every figure in the tables is computed from the model rather than typed in,
 * so the page cannot drift from the curve it is describing — and on the
 * scenario, where the constants are on controls, it moves as they do.
 */

import { useEffect } from 'react';

import {
  GAME_COST,
  TESTING_FEED,
  costCompoundingFactor,
  costCrossoverEvo,
  costDoublingPoints,
  costInflationPerPoint,
  costLinearFactor,
  costLoopEvo,
  costLoopMutagen,
  costPointIndex,
  costPointPrice,
  formatCompact,
  formatEvo,
  formatInt,
  toDisplayMutagen,
  type Ruleset,
} from '../../model';
import { useLab } from '../LabContext';
import { useRouter } from '../router';
import {
  EvolutionExplorer,
  closedFormPremium,
  powerParamsFor,
  type PowerParams,
} from './EvolutionExplorer';

const LOOP_SECTIONS = [
  { id: 'price', label: 'What a point costs' },
  { id: 'regions', label: 'Which part rules where' },
  { id: 'explorer', label: 'Evolution explorer' },
];

const POWER_SECTIONS = [
  { id: 'price', label: 'What a point costs' },
  { id: 'regions', label: 'One exponent, everywhere' },
  { id: 'explorer', label: 'Evolution explorer' },
];

/**
 * The evolutions every price table is quoted at, whichever branch is up. 150%
 * is in the list because the tail is where the branches differ: live's price is
 * frozen there, the 3v3 test's is still climbing.
 */
const PRICE_ROWS = [0.1, 0.25, 0.5, 0.75, 1, 1.5];

/** `b = k·ln 10` — the compounding, written for the continuous integral. */
const B = GAME_COST.compounding * Math.LN10;
/** `10^k` — the factor one evolution point multiplies the price by. */
const RATIO = 10 ** GAME_COST.compounding;

const inflationLabel = `+${(costInflationPerPoint(GAME_COST) * 100).toFixed(2)}%`;
const doublingLabel = costDoublingPoints(GAME_COST).toFixed(0);
const crossover = costCrossoverEvo(GAME_COST);
const totalTo100 = costLoopMutagen(1, GAME_COST);
const totalTo90 = costLoopMutagen(0.9, GAME_COST);
const firstPrice = costPointPrice(1, GAME_COST);
const lastPrice = costPointPrice(101, GAME_COST);

function MathBlock({ children }: { children: React.ReactNode }) {
  return (
    <details className="math">
      <summary>The math</summary>
      <div className="math-body">{children}</div>
    </details>
  );
}

/* ========================================================== live: the loop */

/**
 * The two factors and their product.
 *
 * Keyed by evolution, on the same rows and with the same first and last columns
 * as the power-law branches' table below — so the two can be read against each
 * other line for line, and what live adds is visibly the two middle columns.
 */
function PriceTable() {
  const first = costPointPrice(costPointIndex(0), GAME_COST);
  return (
    <table className="doc-table">
      <thead>
        <tr>
          <th>at evo</th>
          <th>mutagen spent to here</th>
          <th>the line</th>
          <th>the compounding</th>
          <th>price of the next point</th>
          <th>× the first</th>
        </tr>
      </thead>
      <tbody>
        {PRICE_ROWS.map((evo) => {
          const n = costPointIndex(evo);
          const price = costPointPrice(n, GAME_COST);
          return (
            <tr key={evo}>
              <td>{formatEvo(evo, 0)}</td>
              <td>{formatCompact(toDisplayMutagen(costLoopMutagen(evo, GAME_COST)), 1)}</td>
              <td>×{costLinearFactor(n, GAME_COST).toFixed(0)}</td>
              <td>×{costCompoundingFactor(n, GAME_COST).toFixed(2)}</td>
              <td>{formatCompact(toDisplayMutagen(price), 1)}</td>
              <td>{formatCompact(price / first, 1)}×</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function WhatAPointCosts() {
  return (
    <section id="price" className="doc-section">
      <h3>1 · What a point of evolution costs</h3>
      <p className="doc-lede">
        Evolution is bought a percentage point at a time, and the price of the next point is two
        things multiplied together: <b>a line</b> that counts 1, 2, 3 … as you climb, and{' '}
        <b>a compounding surcharge</b> of {inflationLabel} applied again on every point.
      </p>
      <p className="formula">
        price of the n-th evolution point = n × {RATIO.toFixed(3)}ⁿ / 200
      </p>
      <p>
        That is not a fit or an approximation — it is what the loop inside{' '}
        <code>calc_feed_effects</code> charges, one iteration at a time. Below 100% evolution each
        iteration buys exactly one point, so the mutagen behind any evolution is this expression
        summed from 1 up to the point you want.
      </p>
      <PriceTable />
      <p>
        The line is a <b>multiplier</b>, not an amount: the second point costs twice the first, the
        fifty-first costs fifty-one times it. Multiply the two columns out and the last point of the
        run costs <b>{formatInt(lastPrice / firstPrice)}×</b> what the first one did —{' '}
        {formatCompact(toDisplayMutagen(firstPrice), 0)} against{' '}
        {formatCompact(toDisplayMutagen(lastPrice), 0)} mutagen. Summed, the whole climb to 100% is{' '}
        {formatCompact(toDisplayMutagen(totalTo100), 1)}, of which{' '}
        <b>
          the last ten points alone are{' '}
          {(((totalTo100 - totalTo90) / totalTo90) * 100).toFixed(0)}%
        </b>{' '}
        of everything spent reaching 90%.
      </p>

      <MathBlock>
        <p>
          The loop holds the price as its reciprocal, a rate, so it can multiply food by it rather
          than divide:
        </p>
        <p className="formula">dE/dM = 2 / (u · 10^0.015u), u = 100·min(E,1) + 1</p>
        <p>
          <code>u</code> is the point index — 1 at evolution 0, 101 at 100%. Inverting that, and
          pricing a single point rather than a whole unit of evolution, is where the ÷200 comes
          from: the <code>× 0.5</code> in the source, and a hundredth for the point.
        </p>
        <p>
          The clamp on <code>u</code> is what ends the story at 100%. Both factors freeze at their
          n = 101 values, so every further point costs a flat{' '}
          {formatCompact(toDisplayMutagen(lastPrice), 0)} mutagen and evolution becomes linear in
          what is fed — which is the last row of the table above, and the straight run at the top
          of the plot below whenever the zoom is set past 100%. The two power-law branches do
          something else there, which is why the table and the plot both carry on past the cap.
        </p>
        <p className="doc-dim">
          <code>feeding_calculations.lua:30-58</code> — <code>calc_feed_effects</code>.
        </p>
      </MathBlock>
    </section>
  );
}

/** How hard each factor is pushing the price up, at a handful of evolutions. */
function ShareTable() {
  const rows = [0.05, 0.1, 0.2, crossover ?? 0.28, 0.5, 0.9, 1];
  return (
    <table className="doc-table">
      <thead>
        <tr>
          <th>at evo</th>
          <th>the line adds</th>
          <th>the compounding adds</th>
          <th>line’s share</th>
          <th>price rise per point</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((evo) => {
          const n = evo * 100 + 1;
          const line = GAME_COST.linear / costLinearFactor(n, GAME_COST);
          const rise = (1 + 1 / n) * RATIO - 1;
          return (
            <tr key={evo}>
              <td>{formatEvo(evo, 0)}</td>
              <td>{line.toFixed(4)}</td>
              <td>{B.toFixed(4)}</td>
              <td>{((line / (line + B)) * 100).toFixed(0)}%</td>
              <td>+{(rise * 100).toFixed(1)}%</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Where doubling every flask you have ever fed actually lands you. */
function CompressionTable() {
  const doubleFrom = (evo: number) => costLoopEvo(2 * costLoopMutagen(evo, GAME_COST), GAME_COST);
  return (
    <table className="doc-table">
      <thead>
        <tr>
          <th>from</th>
          <th>mutagen spent</th>
          <th>double it and you reach</th>
          <th>gain</th>
        </tr>
      </thead>
      <tbody>
        {[0.3, 0.5, 0.7, 0.9].map((evo) => (
          <tr key={evo}>
            <td>{formatEvo(evo, 0)}</td>
            <td>{formatCompact(toDisplayMutagen(costLoopMutagen(evo, GAME_COST)), 1)}</td>
            <td>{formatEvo(doubleFrom(evo), 1)}</td>
            <td>+{((doubleFrom(evo) - evo) * 100).toFixed(1)} points</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function WhichPartRules() {
  return (
    <section id="regions" className="doc-section">
      <h3>2 · Which part rules where</h3>
      <p className="doc-lede">
        The two factors do not share the work evenly. What matters is not their size but how fast
        each one is <i>rising</i>, and by that measure they swap places exactly once — at{' '}
        <b>{crossover === null ? '—' : formatEvo(crossover, 0)} evolution</b>.
      </p>
      <p>
        The line’s push fades. Going from multiplier 1 to 2 is +100%, but from 50 to 51 is +2%. The
        compounding’s push never fades: it is {inflationLabel} per point, from the first point to
        the last. So the line is the whole story early, the two tie in the high twenties, and past
        that the surcharge is nearly all of it.
      </p>
      <ShareTable />
      <p>
        <b>So the cost is never linear, and never quite exponential either.</b> It always compounds,
        but the compounding rate itself decays — starting near +20% a point and settling towards a
        floor of {inflationLabel}. A true exponential would hold one rate throughout, and evolution
        is switched off at 100% before it ever reaches that floor.
      </p>
      <p>
        {inflationLabel} a point is an inflation rate with one evolution point as its unit of time,
        so the rule of 70 reads it straight off: the price of a point{' '}
        <b>doubles every {doublingLabel} points</b>, and the compounding alone leaves the last point
        of the run{' '}
        {(costCompoundingFactor(101, GAME_COST) / costCompoundingFactor(1, GAME_COST)).toFixed(0)}×
        dearer than the first.
      </p>
      <p>
        <b>What that buys is compression.</b> Because the price compounds, an economy twice the size
        does not buy anything like twice the evolution:
      </p>
      <CompressionTable />
      <p>
        A team out-producing the other two to one is a dozen or so evolution points ahead, not twice
        as strong. That is a catch-up mechanic living in the price list rather than in a rule.
      </p>

      <MathBlock>
        <p>
          Write the price as <code>(1 + a(n−1)) · e^(bn)</code>, with <code>a = 1</code> for the
          line and <code>b = k·ln 10 = {B.toFixed(6)}</code> for the compounding. How fast it is
          rising is the derivative of its log:
        </p>
        <p className="formula">d ln(price)/dn = a / (1 + a(n−1)) + b</p>
        <p>
          First term the line, second the compounding. They are equal at{' '}
          <code>n = 1 + 1/b − 1/a</code>, which at the game’s values is{' '}
          <code>n = {(1 / B - 1 / GAME_COST.linear + 1).toFixed(2)}</code>, or{' '}
          {crossover === null ? '—' : formatEvo(crossover, 2)} evolution.
        </p>
        <p>
          The same constant turns up in the closed form of the total, where{' '}
          <code>1/b = {(1 / B).toFixed(5)}</code> is the root of its bracket.
        </p>
      </MathBlock>
    </section>
  );
}

/* ================================================== 3v3 and the scenario */

/** The branch's own price of a point, one evolution point wide. */
const pointPriceOf = (ruleset: Ruleset, evo: number) =>
  ruleset.mutagenForEvo(evo + 0.01, evo);

function PowerPriceTable({ ruleset }: { ruleset: Ruleset }) {
  const rows = PRICE_ROWS;
  const first = pointPriceOf(ruleset, 0);
  return (
    <table className="doc-table">
      <thead>
        <tr>
          <th>at evo</th>
          <th>mutagen spent to here</th>
          <th>price of the next point</th>
          <th>× the first</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((evo) => {
          const price = pointPriceOf(ruleset, evo);
          return (
            <tr key={evo}>
              <td>{formatEvo(evo, 0)}</td>
              <td>{formatCompact(toDisplayMutagen(ruleset.mutagenForEvo(evo, 0)), 1)}</td>
              <td>{formatCompact(toDisplayMutagen(price), 1)}</td>
              <td>{first > 0 ? `${formatCompact(price / first, 1)}×` : '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PowerWhatAPointCosts({ ruleset, params }: { ruleset: Ruleset; params: PowerParams }) {
  const exponent = 1 / params.power;
  const priceExponent = exponent - 1;
  const flatPastCap = params.scale / params.power;

  return (
    <section id="price" className="doc-section">
      <h3>1 · What a point of evolution costs</h3>
      <p className="doc-lede">
        There is no loop here and no price list. Evolution is one closed form in the{' '}
        <i>total mutagen ever fed</i>, with two constants: <b>S</b>, what a full run to 100% costs,
        and <b>p</b>, the diminishing return itself.
      </p>
      <p className="formula">
        E = (M / {fmt(params.scale, 2)}) ^ {fmt(params.power)}
        {'   ⟺   '}M = {fmt(params.scale, 2)} · E ^ {fmt(exponent, 3)}
      </p>
      <p>
        Read the second form and the price of a point falls out: to be at evolution E you must have
        fed <code>E^{fmt(exponent, 2)}</code> of a full run, so the cost of the <i>next</i> point
        grows like <code>E^{fmt(priceExponent, 2)}</code>. Nothing compounds and nothing is
        iterated — where you are is the only thing that sets what the next point costs.
      </p>
      <PowerPriceTable ruleset={ruleset} />
      <p>
        The whole climb to 100% is {formatCompact(toDisplayMutagen(params.scale), 1)} mutagen by
        definition — that is what <b>S</b> means — and the hundredth point of it costs{' '}
        <b>
          {formatCompact(
            pointPriceOf(ruleset, 0.99) / Math.max(pointPriceOf(ruleset, 0), 1e-12),
            0,
          )}
          ×
        </b>{' '}
        what the first one did.
        {params.stopAt100 ? (
          <>
            {' '}
            Past 100% the price stops rising altogether: the curve switches to its own tangent, so
            every further +100% of evolution costs a flat{' '}
            {formatCompact(toDisplayMutagen(flatPastCap), 1)} mutagen however high you already are.
          </>
        ) : (
          <>
            {' '}
            And it never stops: there is no cap and no tangent, so the price keeps climbing on the
            same power for as long as the game runs.
          </>
        )}
      </p>

      <MathBlock>
        <p>Differentiating the inverse gives the price of a point directly:</p>
        <p className="formula">
          dM/dE = (S / p) · E ^ (1/p − 1) = {fmt(flatPastCap, 1)} · E ^ {fmt(priceExponent, 3)}
        </p>
        <p>
          At E = 1 that is <code>S / p</code> = {fmt(flatPastCap, 1)} mutagen per unit of evolution,
          or {formatCompact(toDisplayMutagen(flatPastCap / 100), 1)} per point — which is exactly
          the flat rate the tangent uses past 100%, and why joining them there leaves no kink.
        </p>
        <p>
          The exponent is <code>1/p − 1</code>, so <code>p</code> is doing two jobs at once: it sets
          how steeply the price climbs, and — because the same <code>p</code> is the exponent on the
          curve itself — how much evolution any amount of mutagen buys. There is no second constant
          to trade against it.
        </p>
        <p className="doc-dim">
          {ruleset.id === 'scenario' ? (
            <>
              <code>feeding_params.lua</code> — <code>evo_for_mutagen</code>, with the constants
              live on this page's controls.
            </>
          ) : (
            <>
              <code>feeding_calculations.lua</code> on <code>{ruleset.branch}</code> —{' '}
              <code>evo_scale = {TESTING_FEED.evoScale}</code>,{' '}
              <code>evo_power = {TESTING_FEED.evoPower}</code>.
            </>
          )}
        </p>
      </MathBlock>
    </section>
  );
}

/** Doubling every flask ever fed, on a curve with no regions in it. */
function PowerCompressionTable({ ruleset }: { ruleset: Ruleset }) {
  return (
    <table className="doc-table">
      <thead>
        <tr>
          <th>from</th>
          <th>mutagen spent</th>
          <th>double it and you reach</th>
          <th>gain</th>
        </tr>
      </thead>
      <tbody>
        {[0.3, 0.5, 0.7, 0.9].map((evo) => {
          const reached = ruleset.evoForMutagen(2 * ruleset.mutagenForEvo(evo, 0));
          return (
            <tr key={evo}>
              <td>{formatEvo(evo, 0)}</td>
              <td>{formatCompact(toDisplayMutagen(ruleset.mutagenForEvo(evo, 0)), 1)}</td>
              <td>{formatEvo(reached, 1)}</td>
              <td>+{((reached - evo) * 100).toFixed(1)} points</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PowerOneExponent({ ruleset, params }: { ruleset: Ruleset; params: PowerParams }) {
  const doubling = 2 ** params.power;
  return (
    <section id="regions" className="doc-section">
      <h3>2 · One exponent, everywhere</h3>
      <p className="doc-lede">
        Live's curve has regions — a stretch where a linear multiplier drives the price, and a
        stretch past about 28% where a compounding surcharge does. This one has none.{' '}
        <b>Doubling the mutagen multiplies evolution by ×{doubling.toFixed(3)}</b>, and it does that
        at 20% evolution and at 200% alike.
      </p>
      <p>
        That is what a power law <i>is</i>: scale-free. There is no crossover to name, no point
        where the shape changes character, and no rate that decays towards a floor. One number,{' '}
        <code>p = {fmt(params.power)}</code>, sets the compression everywhere on the curve.
      </p>
      <PowerCompressionTable ruleset={ruleset} />
      <p>
        The compression is real and it is the catch-up mechanic — a team out-producing the other two
        to one is {((doubling - 1) * 100).toFixed(0)}% further up in evolution, not twice as far — but unlike live it is the <b>same</b> ratio wherever the game is. Live's own gain
        varies with where you start, because its two factors trade places along the way.
      </p>
      {params.stopAt100 ? (
        <p>
          <b>Past 100% the compression stops.</b> The tangent makes evolution linear in mutagen from
          there on, so above the cap doubling what you have fed roughly doubles the evolution again.
          The diminishing return applies to the first 100% and to nothing after it — which is the
          one deliberate discontinuity in the family, and the reason the switch exists.
        </p>
      ) : (
        <p>
          <b>And it never stops.</b> With no cap the same exponent runs past 100% and keeps running:
          where live's price freezes at a flat rate once <code>u</code> clamps, this branch charges
          more for every point forever. That is why it is cheaper than live early and steadily
          dearer late.
        </p>
      )}

      <MathBlock>
        <p>
          Multiply the mutagen by any factor <code>f</code> and the evolution multiplies by{' '}
          <code>f^p</code>, since <code>((fM)/S)^p = f^p · (M/S)^p</code>. The factor does not
          depend on <code>M</code>, which is the whole property:
        </p>
        <p className="formula">
          E(2M) / E(M) = 2^p = {doubling.toFixed(4)}, at every M
        </p>
        <p>
          The elasticity form says the same thing: <code>dE/E = p · dM/M</code>. One percent more
          mutagen is always {fmt(params.power * 100, 1)}% more evolution.
        </p>
        {params.stopAt100 ? (
          <p>
            Above the cap the curve is <code>E = 1 + (M − S)·p/S</code>, so the ratio above is
            replaced by a straight line and the elasticity climbs back towards 1 as M grows.
          </p>
        ) : null}
      </MathBlock>
    </section>
  );
}

/* ================================================================ explorer */

function Explorer({ ruleset, params }: { ruleset: Ruleset; params: PowerParams }) {
  return (
    <section id="explorer" className="doc-section">
      <h3>3 · Evolution explorer</h3>
      <p className="doc-lede">
        The curve with its two constants on sliders, over the app's shared zoom — so the same
        window follows you between the branches, and between this page and the charts.
      </p>
      {ruleset.id === 'live' ? (
        <p>
          <b>Game</b> is the loop the scenario runs. <b>Closed form</b> is that same price
          integrated rather than summed — it charges{' '}
          {(closedFormPremium(GAME_COST) * 100).toFixed(1)}% more to reach 100%, because the loop
          reads the rate at the <i>start</i> of each step and so under-prices every one of them; it
          stops at the cap, where the integral stops being defined. Move a slider and a third curve
          appears: the same loop with your constants, against the game's for comparison.
        </p>
      ) : ruleset.id === 'scenario' ? (
        <p>
          <b>These sliders are the scenario itself</b>, not a local what-if — the same{' '}
          <code>S</code> and <code>p</code> the charts page and <code>feeding_params.lua</code> use,
          so moving one here moves every chart in the app. <b>Live</b> behind it is the loop being
          replaced.
        </p>
      ) : (
        <p>
          <b>3v3 test</b> is the branch as it ships, with <b>Live</b> behind it as the loop it
          replaces. Move a slider and a dashed third curve appears: the same shape with your
          constants, against the branch's for comparison.
        </p>
      )}
      <EvolutionExplorer />
      {ruleset.id === 'live' ? (
        <MathBlock>
          <p>Integrating the price instead of summing it, same b = k·ln 10:</p>
          <p className="formula">
            M(n) = e^(bn) · [ (1 − a + a·n)/b − a/b² ] / 200, measured from n = 1
          </p>
          <p>
            At <code>a = 1</code> that is the <code>F(u)</code> of <code>game-math.md</code> §5.4.
            With the compounding switched off it degenerates into a parabola — the sum of a plain
            line — which is a separate branch in <code>evoCost.ts</code> rather than a division by
            zero.
          </p>
          <p>
            Reading it the other way, evolution from mutagen, has a closed form too: the price is a
            line times an exponential, and undoing that is exactly what the Lambert W function does.
            The explorer bisects instead — exact to double precision, and with no branch cases to
            get wrong.
          </p>
          <p>
            The <b>3v3 branch</b> curve is the shipped example of the simple alternative: no loop at
            all, just <code>E = (M / {TESTING_FEED.evoScale}) ^ {TESTING_FEED.evoPower}</code>. One
            constant, an elementary inverse, and a fixed exponent in place of one that slides from
            about 2 to about 5 across the run.
          </p>
        </MathBlock>
      ) : (
        <MathBlock>
          <p>
            Both directions are elementary here, which is the practical argument for the shape:
          </p>
          <p className="formula">E = (M/S)^p{'    '}M = S · E^(1/p)</p>
          <p>
            No bisection, no Lambert W, no loop to run — a send's result is one <code>pow</code>{' '}
            call, and so is the answer to "what would it take to reach X". The live loop needs a
            hundred iterations for the first and a search for the second.
          </p>
          <p>
            The <b>Live</b> curve drawn behind is the loop of{' '}
            <code>feeding_calculations.lua</code>, whose price of a point is a line times a
            compounding surcharge. Its effective exponent is not fixed: it slides from about 2 early
            to about 5 by 100%, which is what this curve's constant{' '}
            <code>1/p − 1 = {fmt(1 / params.power - 1, 2)}</code> replaces.
          </p>
        </MathBlock>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */

/** Trim to the shortest form that still reads exactly. */
function fmt(value: number, digits = 2): string {
  if (!Number.isFinite(value)) return '—';
  return Number(value.toFixed(digits)).toString();
}

export function UnderstandingEvolution() {
  const { section } = useRouter();
  const { ruleset, scenario } = useLab();
  const loop = ruleset.id === 'live';
  const params = powerParamsFor(ruleset.id, scenario);
  const sections = loop ? LOOP_SECTIONS : POWER_SECTIONS;

  // An (i) that points at a section has to land on it, not on the page top.
  useEffect(() => {
    if (!section) {
      window.scrollTo({ top: 0 });
      return;
    }
    const target = document.getElementById(section);
    if (target) target.scrollIntoView({ block: 'start' });
  }, [section]);

  return (
    <div className="doc">
      <nav className="doc-toc" aria-label="Sections">
        <span className="doc-toc-title">Understanding evolution</span>
        {sections.map((entry) => (
          <a key={entry.id} href={`#${entry.id}`} className="doc-toc-link">
            {entry.label}
          </a>
        ))}
      </nav>
      <div className="doc-body">
        {loop ? (
          <>
            <WhatAPointCosts />
            <WhichPartRules />
          </>
        ) : (
          <>
            <PowerWhatAPointCosts ruleset={ruleset} params={params} />
            <PowerOneExponent ruleset={ruleset} params={params} />
          </>
        )}
        <Explorer ruleset={ruleset} params={params} />
      </div>
    </div>
  );
}
